import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Users, Award, Heart, MapPin } from 'lucide-react';

export const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="relative">
      {/* Hero Section with Full Background */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        {/* Background Image with Stronger Overlay for Better Readability */}
        <div className="absolute inset-0 z-0">
          <img 
            src={process.env.REACT_APP_CONCEPT_FACADE_URL} 
            alt="Doctrina Charter School Campus" 
            className="w-full h-full object-cover"
          />
          {/* Darker overlay for better text readability */}
          <div className="absolute inset-0 bg-navyBlue/95"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl">
            {/* Main Headline - High Contrast */}
            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight">
              Where <span className="text-tealAccent">Teachers Lead</span>,<br />
              <span className="text-goldenOlive">Students Thrive</span>
            </h1>
            
            {/* Subheadline - Improved Readability */}
            <p className="text-2xl sm:text-3xl text-white mb-12 leading-relaxed max-w-3xl font-light">
              A revolutionary middle school experience powered by educator-led innovation and community partnership in California's Inland Empire. Opening Fall 2027 with our founding cohort, expanding to our full 38-acre vision.
            </p>

            {/* Single CTA Button */}
            <div className="mb-16">
              <Button 
                onClick={() => scrollToSection('mission')}
                size="lg" 
                className="bg-tealAccent hover:bg-tealAccent/90 text-white font-bold px-12 py-8 text-xl rounded-xl shadow-2xl hover:shadow-tealAccent/50 transition-all duration-300 hover:scale-105"
              >
                Discover Our Vision
                <ArrowRight className="ml-3" size={24} />
              </Button>
            </div>

            {/* Stats Grid with Better Contrast */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-tealAccent/20">
                <div className="text-5xl font-bold text-tealAccent mb-2">600</div>
                <div className="text-sm text-navyBlue font-semibold">Students</div>
              </div>
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-goldenOlive/20">
                <div className="text-5xl font-bold text-goldenOlive mb-2">38</div>
                <div className="text-sm text-navyBlue font-semibold">Acre Campus</div>
              </div>
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-coralRed/20">
                <div className="text-5xl font-bold text-coralRed mb-2">≤30</div>
                <div className="text-sm text-navyBlue font-semibold">Class Size</div>
              </div>
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-sageGreen/20">
                <div className="text-5xl font-bold text-sageGreen mb-2">5</div>
                <div className="text-sm text-navyBlue font-semibold">Courtyards</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Gradient Fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10"></div>
      </section>

      {/* Key Features Section */}
      <section className="bg-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src={process.env.REACT_APP_LOGO_URL} 
                alt="Doctrina Charter School Logo" 
                className="h-24 sm:h-28 w-auto"
              />
            </div>
            
            <h2 className="text-4xl sm:text-5xl font-bold text-navyBlue mb-4">Why Choose Doctrina?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              An innovative approach to middle school education that puts educators and community first
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Feature 1 */}
            <div className="group relative bg-gradient-to-br from-tealAccent/10 to-tealAccent/5 rounded-3xl p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-tealAccent/20">
              <div className="absolute top-0 right-0 w-20 h-20 bg-tealAccent/20 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="bg-white w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Users className="text-tealAccent" size={32} />
                </div>
                <h3 className="text-xl font-bold text-navyBlue mb-3">Educator-Led</h3>
                <p className="text-gray-600 leading-relaxed">
                  Teachers shape curriculum and guide policy through an elected Academic Senate
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="group relative bg-gradient-to-br from-goldenOlive/10 to-goldenOlive/5 rounded-3xl p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-goldenOlive/20">
              <div className="absolute top-0 right-0 w-20 h-20 bg-goldenOlive/20 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="bg-white w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Award className="text-goldenOlive" size={32} />
                </div>
                <h3 className="text-xl font-bold text-navyBlue mb-3">Research-Based</h3>
                <p className="text-gray-600 leading-relaxed">
                  Proven curricula with all-green EdReports ratings and real-world applications
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="group relative bg-gradient-to-br from-sageGreen/10 to-sageGreen/5 rounded-3xl p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-sageGreen/20">
              <div className="absolute top-0 right-0 w-20 h-20 bg-sageGreen/20 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="bg-white w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Heart className="text-sageGreen" size={32} />
                </div>
                <h3 className="text-xl font-bold text-navyBlue mb-3">Whole Child</h3>
                <p className="text-gray-600 leading-relaxed">
                  Academic excellence balanced with SEL, creativity, and character development
                </p>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="group relative bg-gradient-to-br from-coralRed/10 to-coralRed/5 rounded-3xl p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-coralRed/20">
              <div className="absolute top-0 right-0 w-20 h-20 bg-coralRed/20 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="bg-white w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="text-coralRed" size={32} />
                </div>
                <h3 className="text-xl font-bold text-navyBlue mb-3">Community Hub</h3>
                <p className="text-gray-600 leading-relaxed">
                  Extended hours serving families with education programs and events
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hero;
